import {
  type AirportMap,
  validateAirportMap,
} from "../airport/AirportDefinition";

/**
 * Cloudfield — three runways, match the colours.
 *
 * The runway axis runs approach -> end: aircraft touch down at the approach
 * gate and roll towards the end, then taxi in along `groundRoutes`.
 */
const definition = {
  id: "basic",
  code: "BASE",
  name: "Cloudfield",
  tagline: "Three runways. Match the colors.",
  runways: [
    {
      id: "yellow",
      color: "yellow",
      approach: { x: -50, y: 34 },
      end: { x: 10, y: 34 },
      width: 4.6,
    },
    {
      id: "blue",
      color: "blue",
      approach: { x: 22, y: -40 },
      end: { x: 22, y: 24 },
      width: 4.6,
    },
    {
      id: "red",
      color: "red",
      approach: { x: 40, y: -38 },
      end: { x: 40, y: 26 },
      width: 4.6,
    },
  ],
  groundRoutes: {
    yellow: {
      arrival: [
        { x: 10, y: 29.5 },
        { x: -40, y: 29.5 },
        { x: -40, y: 12 },
        { x: -20, y: 12 },
        { x: -20, y: 0 },
      ],
      departure: [
        { x: -20, y: 12 },
        { x: -44, y: 12 },
        { x: -44, y: 29.5 },
        { x: -50, y: 34 },
      ],
    },
    blue: {
      arrival: [
        { x: 17, y: 24 },
        { x: 17, y: 0 },
        { x: -20, y: 0 },
      ],
      departure: [
        { x: 17, y: 0 },
        { x: 17, y: -40 },
        { x: 22, y: -40 },
      ],
    },
    red: {
      arrival: [
        { x: 35, y: 26 },
        { x: 35, y: 0 },
        { x: -20, y: 0 },
      ],
      departure: [
        { x: 35, y: 0 },
        { x: 35, y: -38 },
        { x: 40, y: -38 },
      ],
    },
  },
  scenery: {
    grassPatches: [
      {
        shade: "dark",
        points: [
          { x: -125, y: -110 },
          { x: -42, y: -120 },
          { x: -8, y: -72 },
          { x: -38, y: -28 },
          { x: -118, y: -42 },
        ],
      },
      {
        shade: "light",
        points: [
          { x: -120, y: 28 },
          { x: -70, y: 14 },
          { x: -18, y: 64 },
          { x: -34, y: 122 },
          { x: -122, y: 112 },
        ],
      },
      {
        shade: "mid",
        points: [
          { x: 42, y: -122 },
          { x: 118, y: -104 },
          { x: 122, y: -30 },
          { x: 84, y: 2 },
          { x: 50, y: -32 },
        ],
      },
      {
        shade: "dark",
        points: [
          { x: 52, y: 34 },
          { x: 122, y: 14 },
          { x: 124, y: 120 },
          { x: 18, y: 122 },
          { x: 8, y: 78 },
        ],
      },
    ],
    dirtFields: [
      { x: -35, y: -4, width: 47, depth: 36 },
      { x: -40, y: 23.5, width: 11, depth: 21 },
      { x: -20, y: 34, width: 72, depth: 11 },
      { x: 14, y: 0, width: 52, depth: 11 },
      { x: 22, y: -8, width: 11, depth: 72 },
      { x: 40, y: -6, width: 11, depth: 72 },
    ],
    stream: {
      width: 15,
      points: [
        { x: 94, y: -126 },
        { x: 83, y: -86 },
        { x: 92, y: -48 },
        { x: 78, y: -8 },
        { x: 88, y: 32 },
        { x: 76, y: 76 },
        { x: 84, y: 126 },
      ],
    },
    trees: [
      { x: -75, y: -56, scale: 1.1 },
      { x: -62, y: -48, scale: 0.8 },
      { x: -82, y: -16, scale: 0.9 },
      { x: -72, y: 58, scale: 1.15 },
      { x: -42, y: 75, scale: 0.85 },
      { x: -8, y: 68, scale: 0.75 },
      { x: 62, y: 58, scale: 1.1 },
      { x: 72, y: 42, scale: 0.75 },
      { x: 69, y: 3, scale: 0.9 },
      { x: 67, y: -57, scale: 1.05 },
      { x: 53, y: -70, scale: 0.75 },
      { x: -5, y: -66, scale: 0.9 },
      { x: -50, y: -73, scale: 0.7 },
    ],
    buildings: [
      { kind: "terminal", x: -35, y: -2, width: 21, depth: 8, height: 3.8 },
      { kind: "hangar", x: -48, y: -16, width: 10, depth: 8, height: 4.5 },
      { kind: "hangar", x: -35, y: -16, width: 9, depth: 7, height: 3.8 },
      { kind: "tower", x: -49, y: 10, width: 5, depth: 5, height: 7 },
    ],
  },
} as const satisfies AirportMap;

export const CLOUDFIELD: AirportMap = validateAirportMap(definition);
